import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, makeStyles, TextField } from '@ellucian/react-design-system/core';
import PropTypes from 'prop-types';
import { CONSTANTS } from '../Helpers/Configuration';
import { useIntl } from 'react-intl';
import LoadingButton from '../Common/LoadingButton';
import { spacing40 } from '@ellucian/react-design-system/core/styles/tokens';

const useStyles = makeStyles(() => ({
    actionsContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
        gap: spacing40
    }
}));

const ApplicantCommentDialog = ({application, onClose, onDeclineConfirmation, isOngoingApiCall, applicantComment, setApplicantComment}) => {
    const { formatMessage } = useIntl();
    const classes = useStyles();
    const { applicationDecisionStatus } = CONSTANTS;
    const [commentError, setCommentError] = useState(false);
    const applicantCommentConfig = {
        [applicationDecisionStatus.admitted]: {
            title: 'applicantHomeAdmittedDeclineConfirmationDialogTitle',
            description: 'applicantHomeAdmittedDeclineConfirmationDialogDescription',
            primaryButton: { label: 'applicantHomeAdmittedDeclineConfirmationDialogAcceptedButton', action: onDeclineConfirmation },
            commentRequired: true,
            commentPlaceholderDescription: 'applicantHomeAdmittedDeclineConfirmationDialogCommentPlaceholder'
        },
        [applicationDecisionStatus.waitlist]: {
            title: 'applicantHomeWaitlistDeclineConfirmationDialogTitle',
            description: 'applicantHomeWaitlistDeclineConfirmationDialogDescription',
            primaryButton: { label: 'applicantHomeWaitlistDeclineConfirmationDialogAcceptedButton', action: onDeclineConfirmation },
            commentRequired: true,
            commentPlaceholderDescription: 'applicantHomeWaitlistDeclineConfirmationDialogCommentPlaceholder'
        }
    };

    const handleCommentBlur = () => {
        if (applicantCommentConfig[application.decisionStatus]?.commentRequired && !applicantComment) {
            setCommentError(true);
        } else {
            setCommentError(false);
        }
    };

    return (
        <Dialog
            open={true}
            onClose={onClose}
            aria-labelledby="form-dialog-title"
            aria-describedby="form-dialog-description"
        >
            <DialogTitle
                id="form-dialog-title"
                aria-label={formatMessage({ id: applicantCommentConfig[application.decisionStatus]?.title })}
            >
                {formatMessage({ id: applicantCommentConfig[application.decisionStatus]?.title })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    id="form-dialog-description"
                    aria-label={formatMessage({ id: applicantCommentConfig[application.decisionStatus]?.description })}
                >
                    {formatMessage({ id: applicantCommentConfig[application.decisionStatus]?.description })}
                </DialogContentText>
                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={10}
                    maxCharacters={200}
                    variant="outlined"
                    value={applicantComment}
                    label={formatMessage({ id: applicantCommentConfig[application.decisionStatus]?.commentPlaceholderDescription })}
                    placeholder={formatMessage({ id: applicantCommentConfig[application.decisionStatus]?.commentPlaceholderDescription })}
                    required={applicantCommentConfig[application.decisionStatus]?.commentRequired}
                    onChange={e => {
                        setApplicantComment(e.target.value);
                        if (applicantCommentConfig[application.decisionStatus]?.commentRequired) {
                            setCommentError(!e.target.value);
                        }
                    }}
                    onBlur={handleCommentBlur}
                    helperText={commentError && formatMessage({ id: 'applicantHomeDeclineCommentValidationError' })}
                    error={commentError}
                />
            </DialogContent>
            <DialogActions>
                <div className={classes.actionsContainer}>
                    <LoadingButton
                        color="primary"
                        onClick={() => {
                            if (applicantCommentConfig[application.decisionStatus]?.commentRequired && !applicantComment) {
                                setCommentError(true);
                                return;
                            }
                            applicantCommentConfig[application.decisionStatus]?.primaryButton?.action();
                        }}
                        isLoading={isOngoingApiCall}
                        isDisabled={commentError || (applicantCommentConfig[application.decisionStatus]?.commentRequired && !applicantComment)}
                    >
                        {formatMessage({ id: applicantCommentConfig[application.decisionStatus]?.primaryButton?.label })}
                    </LoadingButton>
                    <Button
                        color="secondary"
                        onClick={onClose}
                        disabled={isOngoingApiCall}
                    >
                        {formatMessage({ id: 'applicantHomeApplicantCommentCancelButton' })}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

ApplicantCommentDialog.propTypes = {
    application: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onDeclineConfirmation: PropTypes.func,
    onConditionsMetConfirmation: PropTypes.func,
    isOngoingApiCall: PropTypes.bool,
    applicantComment: PropTypes.string,
    setApplicantComment: PropTypes.func
};

export default ApplicantCommentDialog;